import React from 'react';
import { ArrowUpDown, ArrowDownNarrowWide, ArrowDownWideNarrow, Trash2 } from 'lucide-react';

const CreateExpensesTab = ({
  expenseRows, handleAddExpenseRow, handleRemoveExpenseRow, handleExpenseRowChange,
  savedExpenses, handleDeleteSavedExpense, handleSaveExpenseRow,
  expenseOptions,
  expenseSort, setExpenseSort,
  expenseSearch, setExpenseSearch,
  isProjectSaved,
}) => {
  const isRowDirty = (row) => !!(row.expense || row.date || row.memo || row.amount);

  const gridCols = isProjectSaved
    ? 'grid-cols-[160px_120px_1fr_90px_70px_auto_auto]'
    : 'grid-cols-[160px_120px_1fr_90px_70px_auto]';

  return (
    <div>
      <h2 className="text-2xl text-[#5B8DB8] font-light mb-6">Project Expenses</h2>
      
      {/* Expense Entry Form */}
      <div className="mb-6">
        <div className={`grid ${gridCols} gap-2 mb-2`}>
          <div className="text-sm text-gray-600">Expense</div>
          <div className="text-sm text-gray-600">Date</div>
          <div className="text-sm text-gray-600">Memo</div>
          <div className="text-sm text-gray-600">Amount</div>
          <div className="text-sm text-gray-600">Percent</div>
          <div></div>
          {isProjectSaved && <div></div>}
        </div>
        {expenseRows.map((row, index) => {
        const dirty = isRowDirty(row);
        return (
        <div key={index} className={`grid ${gridCols} gap-2 mb-2`}>
          <select
            value={row.expense}
            onChange={(e) => handleExpenseRowChange(index, 'expense', e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            data-testid={`expense-select-${index}`}
          >
            <option value="">-- select --</option>
            {expenseOptions.map((opt, i) => (
              <option key={i} value={opt.name}>{opt.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={row.date}
            onChange={(e) => handleExpenseRowChange(index, 'date', e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            data-testid={`expense-date-${index}`}
          />
          <input
            type="text"
            value={row.memo}
            onChange={(e) => handleExpenseRowChange(index, 'memo', e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            data-testid={`expense-memo-${index}`}
          />
          <input
            type="text"
            value={row.amount}
            onChange={(e) => handleExpenseRowChange(index, 'amount', e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            data-testid={`expense-amount-${index}`}
          />
          <input
            type="text"
            value={row.percent}
            onChange={(e) => handleExpenseRowChange(index, 'percent', e.target.value)}
            className="border border-gray-300 rounded px-2 py-2 text-sm"
            data-testid={`expense-percent-${index}`}
          />
          <div>
            {index > 0 && (
              <button
                onClick={() => handleRemoveExpenseRow(index)}
                className="text-red-500 hover:text-red-700 p-2"
                data-testid={`remove-expense-row-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          {isProjectSaved && (
            <button
              onClick={() => handleSaveExpenseRow(index)}
              disabled={!dirty}
              className={`px-3 py-2 rounded text-sm text-white whitespace-nowrap ${dirty ? 'bg-[#4ECFDB] hover:bg-[#3AB8C5]' : 'bg-gray-300 cursor-not-allowed'}`}
              data-testid={`save-expense-btn-${index}`}
            >
              Save Expense
            </button>
          )}
        </div>
        );
        })}
        
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={handleAddExpenseRow}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors text-sm"
            data-testid="add-expense-btn"
          >
            Add Expense
          </button>
          {!isProjectSaved && expenseRows.some(r => isRowDirty(r)) && (
            <p className="text-xs text-gray-400">Expenses will be saved when you save the project.</p>
          )}
        </div>
      </div>
      
      {/* Search */}
      <div className="flex justify-end mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Search:</span>
          <input
            type="text"
            value={expenseSearch}
            onChange={(e) => setExpenseSearch(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-48"
            data-testid="expense-search"
          />
        </div>
      </div>
      
      {/* Expenses Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2">
              {['expense', 'date', 'memo', 'amount', 'last_updated'].map(col => (
              <th key={col} className="text-left p-2 text-gray-600 font-normal">
                <button onClick={() => setExpenseSort(s => s.column === col ? { column: col, direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: col, direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800">
                  {expenseSort.column === col
                    ? (expenseSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-4 h-4 text-red-500" /> : <ArrowDownWideNarrow className="w-4 h-4 text-red-500" />)
                    : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                  <span className={expenseSort.column === col ? 'text-red-500' : ''}>
                    {col === 'last_updated' ? 'Last Updated On' : col.charAt(0).toUpperCase() + col.slice(1)}
                  </span>
                </button>
              </th>
              ))}
              <th className="text-left p-2 text-gray-600 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let filtered = savedExpenses.filter(e =>
                !expenseSearch || Object.values(e).some(v => String(v).toLowerCase().includes(expenseSearch.toLowerCase()))
              );
              if (expenseSort.column) {
                filtered = [...filtered].sort((a, b) => {
                  const col = expenseSort.column;
                  const dir = expenseSort.direction === 'asc' ? 1 : -1;
                  if (col === 'amount') return ((parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0)) * dir;
                  const aVal = (a[col] || '').toString().toLowerCase();
                  const bVal = (b[col] || '').toString().toLowerCase();
                  return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
                });
              }
              if (filtered.length === 0) {
                return (
                  <tr>
                    <td colSpan="6" className="p-4 text-gray-500 italic">No data available in table</td>
                  </tr>
                );
              }
              return filtered.map((expense, index) => (
                <tr key={index} className="border-b hover:bg-gray-50" data-testid={`saved-expense-row-${index}`}>
                  <td className="p-2 text-gray-700">{expense.expense}</td>
                  <td className="p-2 text-gray-700">{expense.date}</td>
                  <td className="p-2 text-gray-700">{expense.memo}</td>
                  <td className="p-2 text-gray-700">${parseFloat(expense.amount || 0).toFixed(2)}</td>
                  <td className="p-2 text-gray-700">{expense.last_updated}</td>
                  <td className="p-2">
                    <button onClick={() => handleDeleteSavedExpense(index)} className="text-red-500 hover:text-red-700" data-testid={`delete-saved-expense-${index}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>
      
      {/* Total */}
      <div className="mt-4 border-t pt-4">
        <div className="flex">
          <span className="text-gray-700">Total Expenses</span>
          <span className="ml-auto text-gray-700">
            ${savedExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        Showing {savedExpenses.length > 0 ? 1 : 0} to {savedExpenses.length} of {savedExpenses.length} entries
      </div>
    </div>
  );
};

export default CreateExpensesTab;
